/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ComponentType, ReactElement, SVGProps } from 'react';
import { BgBagIcon, NmCartIcon } from '@/components/icons';
import type { Brand } from '@/lib/brand/brand';
import { useBrand } from '@/lib/brand/use-brand';
import { HEADER_COMPOSITION } from '@/lib/brand/header-composition';

/**
 * The bag DRAWING, per brand — component selection by brand key, never an
 * inline branch (established idiom: `MINI_CART_SURFACE` one file over in
 * cart-badge.tsx, `mobileMenuGlyph` in navigation-menu-curated/mobile.tsx,
 * `NAV_SWITCH` in navigation-menu-mega). A third brand is a COMPILE ERROR here
 * rather than a silent inheritance of NM's bag, which is exactly what BG had.
 *
 * WHY A MAP AND NOT A TOKEN, AND WHY THE BOX DID NOT CATCH THIS. Until this
 * landed both brands rendered `NmCartIcon`, with BG merely boxed to its own
 * measured rect — so the box was RIGHT and the drawing was WRONG, and every
 * box-level comparison reported parity. The two marks are different drawings,
 * not one drawing at two scales: NM's is a semicircular handle over a
 * square-cornered body at ink aspect 0.911; live BG's is a squared-off handle
 * over a rounded-corner body at 0.846, carrying an asymmetric interior stub NM
 * has nowhere. No CSS value expresses a different silhouette.
 *
 * `cartGlyphBox` in lib/brand/header-composition.ts carries each brand's BOX
 * and keeps doing so — the box sizes and positions the mark, this map chooses
 * which mark. Both are read one line apart at the render below, the same
 * arrangement the hamburger pair already uses.
 *
 * A POINTER MAP: each arm names a component, so the checkable reading lives one
 * level down, on the path in the file this cites. Both are quoted by
 * `file:line` rather than by name, per arm, because the two brands' standings
 * are not the same and one citation covering both would be false about one of
 * them.
 */
const CART_GLYPH: Record<Brand, ComponentType<SVGProps<SVGSVGElement>>> = {
    // nm — components/icons/nm-cart-icon.tsx:24 is the path. UNMEASURED as a
    // transcription: unlike BG's, this drawing has never been matched against
    // an inline asset in live NM's own markup. NM's dumps carry no
    // `Shopping Bag containing` img and no bag vector was recovered for this
    // change, so it stands on its rendered rect alone — svg 20x17, live NM 1440
    // (harvest/micro-harvest-header2.md §1, quoted at the render below). It is
    // left exactly as it was because NM is not this change's subject, and
    // because replacing it blind would repeat the error this map exists to fix.
    nm: NmCartIcon,
    // bg — components/icons/bg-bag-icon.tsx:118 is the path, byte-identical to
    // the `d` of the inline base64 asset on the `alt="Shopping Bag containing N
    // items"` img in .reference/bg-homepage.mobile.html and three more BG
    // dumps. That file's docblock carries the subpath table, the 21.600 x
    // 25.527 ink and the independent pixel scan that agrees with it.
    bg: BgBagIcon,
};

/**
 * The MOBILE count overlaid on the bag art, per brand — a COMPLETE class string
 * so Tailwind's scanner can see it, brand-keyed rather than an inline branch
 * (same idiom as `CART_GLYPH` above and `MINI_CART_SURFACE` in cart-badge.tsx).
 *
 * THE TWO BRANDS DO NOT RENDER THE SAME THING, so this is a whole class string
 * and not an offset token. It replaces the former `CART_BADGE_ANCHOR`, whose
 * shape — one shared string with a per-brand `top-*` swapped into it — encoded
 * the claim that both brands paint a transparent 14px digit inside the bag
 * body and differ only in how deep the body starts. That claim is true of live
 * NM and FALSE of live BG, so no single string could carry both.
 *
 * ── nm: the count is IN THE BAG BODY, below the handle ──
 *
 * harvest/micro-harvest-header2.md §1, quoted in full at the render below:
 * container 20x26, svg 20x17, count span 14x14 horizontally centred with its
 * top 4px below the svg top, type 400 10px centred, inheriting the header
 * foreground. NM's arm is BYTE-IDENTICAL to the string this file rendered
 * before — same tokens, same order — so NM's rendered class attribute and its
 * pinned storybook snapshots are untouched BY CONSTRUCTION, not by luck.
 *
 * ── bg: the count is a BLACK CIRCLE PINNED TOP-RIGHT OF THE ART ──
 *
 * Named in words first, because a selector is not an identity: live BG's
 * mobile count is the LAST CHILD of the mobile bag LINK — an `<a
 * class="minicart-dt__mobile__link" href="/cart/">` holding a 30x30 inline-SVG
 * bag `<img alt="Shopping Bag containing 1 items">` and, after it, a `<span
 * class="minicart-dt__mobile__qty">1</span>`. The link is the positioning
 * context (`display:inline-block;position:relative`); the span is out of flow
 * on top of the art. Read at .reference/bg-homepage.mobile.html offsets
 * 78615..79436, which is a NON-EMPTY capture — its desktop sibling reads
 * `<span class="minicart-dt__container__header__qty">1</span>`. The same link
 * in .reference/dumps/bg-pdp-m-390/mob-pdp-390-bg.html (offset 8641970) holds
 * the img and NOTHING ELSE: that bag is empty, and an empty-bag capture is
 * structurally incapable of witnessing a count. That pair is also the evidence
 * for the `numberOfItems > 0` gate at the render below.
 *
 * EVERY DECLARATION BELOW IS LIVE'S OWN, from live BG's shipped stylesheet at
 * the hash its own <link rel="stylesheet"> names —
 * .reference/dumps/bg-plp-d-1280/dt-plp-1280-bg_files/
 * client.d19794a33980fdba6d07.bundle.css, `.minicart-dt__mobile__qty` at byte
 * 404423, and byte-identical in the e910c150… bundle under bg-pdp-m-390/:
 *
 *     position:absolute      -> absolute
 *     left:16px              -> left-[16px]
 *     top:2px                -> top-[2px]
 *     width:20px;height:20px -> size-5
 *     border-radius:100%     -> rounded-full
 *     background:#000        -> bg-primary              (tokens/core.css:13 #000000)
 *     color:#fff             -> text-primary-foreground (tokens/core.css:14 #ffffff)
 *     font-size:12px         -> text-[12px]
 *     font-weight:600        -> font-semibold
 *     line-height:20px       -> leading-[20px]
 *     letter-spacing:.2px    -> tracking-[0.2px]
 *     text-align:center      -> text-center
 *
 * Same two notes as the desktop badge one file over, and for the same reasons.
 * `rounded-full` is 9999px where live writes 100%; on a 20x20 square both paint
 * the same circle. Live's `display:inline-block` is blockified by
 * `position:absolute`, so it is inert and is not transcribed. The semantic
 * colour pair is used because `bg-black`/`text-white` are `custom/color-linter`
 * errors here and `--primary` / `--primary-foreground` are declared once in the
 * shared core with no brand override — they ARE live's #000/#fff, checked.
 *
 * Live's own numbers put the circle at x 16..36 and y 2..22 inside a 30px box,
 * so it OVERHANGS the bag's right edge by 6px. That is not a transcription
 * error; it is what live paints, and our wrapper is `relative inline-flex`
 * around the same 30px glyph, so the two boxes agree.
 *
 * WHAT THIS RETIRES, AND WHY IT IS NOT RE-DERIVED. `CART_BADGE_ANCHOR.bg` was
 * an 11px top offset, DERIVED rather than measured: it centred a 14px count in live
 * BG's bag interior (11.41 -> 24.61 below the box top), which is live's ART but
 * was never live's STYLESHEET. Its own docblock said so and said a capture of
 * BG's header with a non-empty bag would settle it. That capture is the
 * bg-homepage.mobile.html cited above, and it settles it AGAINST the derivation
 * — a corner bubble has no relationship to the bag interior that offset
 * measured, so the value is deleted outright rather than re-derived. Nothing
 * downstream should look for a BG bag-interior depth again.
 */
const MOBILE_BAG_COUNT: Record<Brand, string> = {
    nm: 'absolute top-1 left-1/2 -translate-x-1/2 w-3.5 h-3.5 text-[10px] leading-[14px] font-normal text-center',
    bg: 'absolute left-[16px] top-[2px] size-5 rounded-full bg-primary text-primary-foreground text-[12px] font-semibold leading-[20px] tracking-[0.2px] text-center',
};

/**
 * Live-NM cart badge structure (.reference/harvest/micro-harvest-header2.md §1):
 * the count is NOT a corner bubble — it is an absolutely-positioned span
 * OVERLAID on the bag glyph, a sibling of the svg inside a position:relative
 * wrapper, so the number sits IN the bag body below the handle arc. Live rects
 * (desktop 1440): container 20x26, svg 20x17, count span 14x14 horizontally
 * centered with its top 4px below the svg top; count type 400 10px, centered,
 * inheriting the header foreground. Mobile shares the exact same structure.
 *
 * Absolute positioning inside the icon's own relative wrapper is the sanctioned
 * exception to the no-absolute rule: the overlay is intrinsic to this composite
 * glyph, not page layout.
 *
 * Deliberate divergences from live (both noted in the harvest):
 * - the count renders only when numberOfItems > 0 (live NM keeps an empty span);
 * - no equivalent of live's `--empty` modifier class, which lies — it stays on
 *   the container even with items in the bag, so nothing may key off it.
 * The count is still announced at every value via the trigger button's
 * aria-label in cart-badge.tsx (badge.ariaLabel carries the count).
 *
 * EVERY SENTENCE ABOVE IS ABOUT LIVE NM AND ONLY LIVE NM. It used to read as a
 * statement about the count as such, which is how a BG offset came to be
 * derived from an NM rule. Live BG's mobile count IS a corner bubble, out at
 * the top-right of the art, and its empty-bag markup omits the span entirely
 * rather than keeping an empty one — see `MOBILE_BAG_COUNT` above, which
 * carries live BG's own declaration and both captures.
 */
export default function CartBadgeIcon({ numberOfItems }: { numberOfItems: number }): ReactElement {
    // Brands whose desktop utilities are TEXT LINKS carry no bag glyph at all
    // (BG — bg-home-d-1280/App.tsx:107 is an <a> containing only the word "Bag";
    // the whole right third of that capture holds no <img>/<svg>). THIS OVERLAY
    // is part of the glyph, so it goes with it — but that is a statement about
    // this MECHANISM, not about the count as such, and the earlier revision of
    // this comment conflated the two. Live BG has a SECOND, different count at
    // desktop: a black 20px circle 5px past the word "Bag", gated in live's own
    // stylesheet at exactly this brand's 1025 boundary. It is rendered by
    // `DESKTOP_BAG_COUNT` in cart-badge.tsx, which carries the evidence; the
    // owner reported its absence as the bag not reflecting a count. Nothing on
    // this line changed when that landed. NM's suffix is the empty string —
    // same DOM, same classes. Template strings rather than `cn()`: NM's class
    // strings are pinned byte-for-byte in the storybook snapshots.
    const brand = useBrand();
    const composition = HEADER_COMPOSITION[brand];
    // Brand-keyed DRAWING — see CART_GLYPH above for why the box could never
    // have carried this.
    const CartGlyph = CART_GLYPH[brand];
    const glyphGate = composition.desktopUtilities === 'text-links' ? ` ${composition.mobileOnly}` : '';
    return (
        // THE WRAPPER TAKES THE GATE TOO, not just the glyph it wraps. This span
        // exists solely to be the positioning context for the count overlay, and
        // both of its children already carry `glyphGate` — so on a text-links
        // brand it renders at 0x0 and is still a FLEX ITEM of the trigger
        // Button, whose stock shadcn `gap-2` then spends 8px separating nothing
        // from the word "Bag". Measured in-browser, brand `bg`, clientWidth
        // 1280: the wrapper sat at x=1147.59 with width 0 and the label began at
        // x=1155.59 — exactly 8px later. That is framework chrome standing in
        // the middle of a measured gap; live BG's Bag is a bare anchor whose
        // only spacing is its neighbour's `mr-[26px]` (bg-home-d-1280/App.tsx:99
        // and :107, one occurrence each after halving, control `div` 1042).
        // `display:none` removes it from flex layout entirely, so the gap
        // collapses with it. NM's `glyphGate` is the empty string, so NM's class
        // attribute here is byte-identical to what it was and its pinned
        // storybook snapshots are untouched BY CONSTRUCTION, not by luck.
        <span className={`relative inline-flex${glyphGate}`}>
            {/* The glyph BOX comes from the composition map and the DRAWING
                from CART_GLYPH above — two keys because the brands differ in
                both, independently. See `cartGlyphBox` there for each brand's
                measurement and for why the string must contain a `size-`
                token. NM's box value is byte-identical to the
                `size-5 h-(--header-icon-glyph-cart-height)` this line carried
                inline, so NM's snapshots are untouched; BG's is live's own
                declared 30px box, at which live's own art renders at scale 1
                and inks 21.60 x 25.53 against live's scanned 21.5 x 25.5. */}
            <CartGlyph className={`${composition.cartGlyphBox}${glyphGate}`} data-testid="shopping-cart-icon" />
            {numberOfItems > 0 && (
                <span className={`${MOBILE_BAG_COUNT[brand]}${glyphGate}`} data-testid="shopping-cart-badge">
                    {numberOfItems}
                </span>
            )}
        </span>
    );
}
